import React, { useState, useEffect } from 'react';
import { Card, Button, Table, TableHead, TableBody, TableRow, TableCell, Divider } from '@mui/material';
import ApiVersionSelector from '../components/ApiVersionSelector';
import { versionedApiClient, ApiVersion } from '../api/versionedClient';
import { 
  CLIENT_ENDPOINTS, 
  WORKFLOW_ENDPOINTS,
  MODULE1_ENDPOINTS,
  MODULE2_ENDPOINTS,
  MODULE3_ENDPOINTS,
  MODULE4_ENDPOINTS
} from '../api/endpointMap';
import EndpointAnalyzer from '../utils/endpointAnalyzer';

interface MissingEndpoint {
  endpoint: string;
  version: string;
  timestamp: string;
}

interface EndpointStatus {
  name: string;
  endpoint: any;
  v1Status: 'unknown' | 'available' | 'missing';
  dbStatus: 'unknown' | 'available' | 'missing';
  v2Status: 'unknown' | 'available' | 'missing';
}

/**
 * API Debug Page - Helps developers identify missing endpoints
 */
const ApiDebugPage: React.FC = () => {
  const [endpoints, setEndpoints] = useState<EndpointStatus[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  
  // Define the endpoints to check
  const endpointList = [
    // Client endpoints
    { name: 'List Clients', endpoint: CLIENT_ENDPOINTS.LIST_CLIENTS },
    { name: 'Get Client', endpoint: CLIENT_ENDPOINTS.GET_CLIENT },
    { name: 'Create Client', endpoint: CLIENT_ENDPOINTS.CREATE_CLIENT },
    
    // Workflow endpoints
    { name: 'List Workflows', endpoint: WORKFLOW_ENDPOINTS.LIST_WORKFLOWS },
    { name: 'Get Workflow', endpoint: WORKFLOW_ENDPOINTS.GET_WORKFLOW },
    { name: 'Create Workflow', endpoint: WORKFLOW_ENDPOINTS.CREATE_WORKFLOW },
    
    // Module 1 - Keyword Analysis
    { name: 'Start Analysis', endpoint: MODULE1_ENDPOINTS.START_ANALYSIS },
    { name: 'Get Analysis Status', endpoint: MODULE1_ENDPOINTS.GET_ANALYSIS_STATUS },
    { name: 'Get Analysis Results', endpoint: MODULE1_ENDPOINTS.GET_ANALYSIS_RESULTS },
    { name: 'Verify Analysis', endpoint: MODULE1_ENDPOINTS.VERIFY_ANALYSIS },
    
    // Module 2 - Code Generation
    { name: 'Generate Code', endpoint: MODULE2_ENDPOINTS.GENERATE_CODE },
    { name: 'Get Code Status', endpoint: MODULE2_ENDPOINTS.GET_CODE_STATUS },
    { name: 'Get Code', endpoint: MODULE2_ENDPOINTS.GET_CODE },
    { name: 'Execute Code', endpoint: MODULE2_ENDPOINTS.EXECUTE_CODE },
    
    // Module 3 - Semantic Analysis
    { name: 'Start Semantic Analysis', endpoint: MODULE3_ENDPOINTS.START_SEMANTIC_ANALYSIS },
    { name: 'Get Semantic Status', endpoint: MODULE3_ENDPOINTS.GET_SEMANTIC_STATUS },
    { name: 'Get Framework', endpoint: MODULE3_ENDPOINTS.GET_FRAMEWORK },
    
    // Module 4 - Batch Processing
    { name: 'List Configs', endpoint: MODULE4_ENDPOINTS.LIST_CONFIGS },
    { name: 'Get Config', endpoint: MODULE4_ENDPOINTS.GET_CONFIG },
    { name: 'Start Batch', endpoint: MODULE4_ENDPOINTS.START_BATCH },
    { name: 'Get Batch Status', endpoint: MODULE4_ENDPOINTS.GET_BATCH_STATUS },
  ];
  
  // Display missing endpoints from tracking
  const [missingEndpoints, setMissingEndpoints] = useState<MissingEndpoint[]>([]);
  const [analyzing, setAnalyzing] = useState<boolean>(false);
  const [analysisResult, setAnalysisResult] = useState<{
    missingEndpoints: string[];
    totalEndpoints: number;
  } | null>(null);
  
  useEffect(() => {
    // Load tracked missing endpoints
    if (window.missingDbEndpoints) {
      setMissingEndpoints([...window.missingDbEndpoints]);
    }
    
    // Set up tracking for new missing endpoints - check less frequently
    const intervalId = setInterval(() => {
      if (window.missingDbEndpoints) {
        // Only update if there are new endpoints or different count
        if (missingEndpoints.length !== window.missingDbEndpoints.length) {
          setMissingEndpoints([...window.missingDbEndpoints]);
        }
      }
    }, 5000); // Check every 5 seconds instead of every 2 seconds
    
    return () => clearInterval(intervalId);
  }, [missingEndpoints.length]);
  
  // Helper to check an endpoint's availability
  const checkEndpoint = async (endpoint: any, version: ApiVersion) => {
    try {
      // Extract the endpoint string for the requested version
      const endpointString = typeof endpoint === 'string' 
        ? endpoint 
        : endpoint[version as keyof typeof endpoint];
      
      if (!endpointString) {
        return { status: 'unknown', message: 'Endpoint not defined for this version' };
      }
      
      if (endpointString.includes('{id}')) {
        return { status: 'unknown', message: 'Requires ID parameter' };
      }
      
      // Make the request using the specific version
      await versionedApiClient.get(
        endpointString,
        {},
        {},
        version
      );
      
      return { status: 'available', message: 'Endpoint is available' };
    } catch (error: any) {
      // Don't log the error here, as it's expected when checking for endpoints
      if (error.response && error.response.status === 404) {
        return { status: 'missing', message: 'Endpoint is missing (404)' };
      } else {
        return { 
          status: error.response ? `error-${error.response.status}` : 'error',
          message: error.message || 'Unknown error'
        };
      }
    }
  };
  
  // Check all endpoints
  const checkAllEndpoints = async () => {
    setIsChecking(true);
    
    const results: EndpointStatus[] = [];
    
    for (const endpointInfo of endpointList) {
      const v1Result = await checkEndpoint(endpointInfo.endpoint, 'v1');
      const dbResult = await checkEndpoint(endpointInfo.endpoint, 'db');
      const v2Result = await checkEndpoint(endpointInfo.endpoint, 'v2');
      
      results.push({
        name: endpointInfo.name,
        endpoint: endpointInfo.endpoint,
        v1Status: v1Result.status as any,
        dbStatus: dbResult.status as any,
        v2Status: v2Result.status as any
      });
    }
    
    setEndpoints(results);
    setIsChecking(false);
  };
  
  // Run a comprehensive analysis of all endpoints
  const runEndpointAnalysis = async () => {
    setAnalyzing(true);
    try {
      const result = await EndpointAnalyzer.checkMissingDbEndpoints();
      setAnalysisResult(result);
    } catch (error) {
      console.error('Error analyzing endpoints:', error);
    } finally {
      setAnalyzing(false);
    }
  };
  
  return (
    <div className="container mx-auto py-8">
      <h1 className="text-3xl font-bold mb-6">API Debug Tools</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">API Version Control</h2>
          <p className="text-sm text-gray-500 mb-4">
            Switch between different API versions to test compatibility.
          </p>
          
          <div className="flex items-center">
            <ApiVersionSelector />
          </div>
        </Card>
        
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">Endpoint Status Check</h2>
          <p className="text-sm text-gray-500 mb-4">
            Check the availability of endpoints across all API versions.
          </p>
          
          <Button 
            variant="contained" 
            color="primary"
            onClick={checkAllEndpoints}
            disabled={isChecking}
          >
            {isChecking ? 'Checking...' : 'Check All Endpoints'}
          </Button>
        </Card>
      </div>
      
      {endpoints.length > 0 && (
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Endpoint Status Results</h2>
          
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>v1</TableCell>
                <TableCell>db</TableCell>
                <TableCell>v2</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {endpoints.map((endpoint, index) => (
                <TableRow key={index}>
                  <TableCell>{endpoint.name}</TableCell>
                  <TableCell>
                    <span className={
                      endpoint.v1Status === 'available' ? 'text-green-600 font-bold' : 
                      endpoint.v1Status === 'missing' ? 'text-red-600 font-bold' : 
                      'text-yellow-600'
                    }>
                      {endpoint.v1Status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={
                      endpoint.dbStatus === 'available' ? 'text-green-600 font-bold' : 
                      endpoint.dbStatus === 'missing' ? 'text-red-600 font-bold' : 
                      'text-yellow-600'
                    }>
                      {endpoint.dbStatus}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={
                      endpoint.v2Status === 'available' ? 'text-green-600 font-bold' : 
                      endpoint.v2Status === 'missing' ? 'text-red-600 font-bold' : 
                      'text-yellow-600'
                    }>
                      {endpoint.v2Status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      
      {missingEndpoints.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">
            Missing Endpoints Detected ({missingEndpoints.length})
          </h2>
          
          <div className="max-h-[400px] overflow-y-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Endpoint</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>First Detected</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {missingEndpoints.map((endpoint, index) => (
                  <TableRow key={index}>
                    <TableCell>{endpoint.endpoint}</TableCell>
                    <TableCell>{endpoint.version.toUpperCase()}</TableCell>
                    <TableCell>
                      {new Date(endpoint.timestamp).toLocaleTimeString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          <Button 
            variant="outlined" 
            color="secondary"
            className="mt-4"
            onClick={() => {
              if (window.missingDbEndpoints) {
                window.missingDbEndpoints = [];
                setMissingEndpoints([]);
              }
            }}
          >
            Clear Tracked Endpoints
          </Button>
        </Card>
      )}
      
      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Endpoint Analyzer</h2>
        <p className="text-sm text-gray-500 mb-4">
          Run a comprehensive check of all API endpoints to identify which ones are missing.
        </p>
        
        <Button 
          variant="contained" 
          color="primary"
          onClick={runEndpointAnalysis}
          disabled={analyzing}
        >
          {analyzing ? 'Analyzing...' : 'Analyze All Endpoints'}
        </Button>
        
        {analysisResult && (
          <div className="mt-4">
            <Divider className="my-4" />
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold">Analysis Results</h3>
              <span className="text-sm bg-blue-100 px-2 py-1 rounded">
                {analysisResult.missingEndpoints.length} missing of {analysisResult.totalEndpoints} total
              </span>
            </div>
            {analysisResult.missingEndpoints.length > 0 ? (
              <div className="max-h-[200px] overflow-y-auto border rounded p-2">
                <ul className="list-disc pl-5">
                  {analysisResult.missingEndpoints.map((endpoint, index) => (
                    <li key={index} className="text-sm text-red-600">
                      {endpoint}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-green-600">All endpoints implemented!</p>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ApiDebugPage; 